/**
 * The wire shapes every domain shares, hand-written from `components.schemas` in
 * `docs/api/openapi.yaml`. Resource types are hand-written per domain as well
 * (`lib/api/<domain>/types.ts`, first `ledger`); a generator is not worth its keep for a handful of shapes.
 */

import { format } from 'date-fns'

/**
 * A contract enum as a `{ value: value }` object: `X.value` reads like a member and `Object.values(X)`
 * lists the values in contract order. `enums-contract.test.ts` checks every one against `docs/api/paths`.
 */
export const strEnum = <const T extends string>(values: readonly T[]): { readonly [K in T]: K } =>
  Object.fromEntries(values.map((value) => [value, value])) as { readonly [K in T]: K }

/** `EntryStatus`: the life cycle every bookable entry (expense, income, transfer, salary) shares. */
export const EntryStatusType = strEnum(['draft', 'to_book', 'booked', 'canceled', 'private_charge'])
export type EntryStatusTypeValue = keyof typeof EntryStatusType

export const PROBLEM_MEDIA_TYPE = 'application/problem+json'

/** RFC 9457 problem detail (`ProblemDetails`), the body of every backend error. */
export type ProblemDetails = {
  /** URI identifying the problem type; `about:blank` when the status code says it all. */
  type?: string
  title: string
  status: number
  detail?: string
  instance?: string
  /** Per-field validation errors on a 422. */
  errors?: { field: string; detail: string }[]
  /** Echoed on a 500 so a user report can be matched to the server log. */
  correlationId?: string
  [extension: string]: unknown
}

/** Pagination metadata shared by every collection envelope (`PageMeta`). */
export type PageMeta = {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

/** Collection envelope (`Page`): `{ items, page, pageSize, total, totalPages }`. */
export type Page<T> = PageMeta & { items: T[] }

/**
 * The items of a collection response. Checks the envelope is really there: during the migration a
 * path can still be answered by a Next handler with a bare array, and reading `.items` off that
 * would silently yield `undefined` rows.
 */
export function unwrapPage<T>(body: unknown): T[] {
  const items = (body as Partial<Page<T>> | null)?.items
  if (!Array.isArray(items)) throw new TypeError('Expected a collection envelope with an `items` array')
  return items
}

/**
 * A `Date` as the calendar day the user picked (`YYYY-MM-DD`), which is what the write schemas
 * take for `issued`. `format` reads the value's own zone — the pickers are pinned to UTC (#768) —
 * so the day that was clicked is the day that travels.
 */
export function toApiDay(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}
